import { BookingConfirmPanel } from '@/features/booking/booking-confirm-panel'
import { BookingExpiryCountdown } from '@/features/booking/booking-expiry-countdown'
import { BookingHeader } from '@/features/booking/booking-header'
import { resolveBookingLanguage } from '@/features/booking/booking-language'
import { BookingLanguageSelector } from '@/features/booking/booking-language-selector'
import { CalendarDayColumns } from '@/features/booking/calendar-day-columns'
import { findFirstWeekWithRegions, parseRegions, slotToIso } from '@/features/booking/calendar-math'
import { CalendarWeekView } from '@/features/booking/calendar-week-view'
import { TerminalStateScreen } from '@/features/booking/terminal-state-screen'
import { TimezoneSelector } from '@/features/booking/timezone-selector'
import type { SelectedSlot } from '@/features/booking/types'
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/locales'
import { logger } from '@/ui'
import {
	publicBookingRegionsQueryKey,
	usePublicBookingConfirm,
	usePublicBookingRegions,
	usePublicBookingShow,
} from '@jobilla/nexus-sdk'
import type { PublicBookingConfirmError } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { AxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/book/$token')({
	component: RouteComponent,
})

function useIsMobile() {
	const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
	useEffect(() => {
		const handler = () => setIsMobile(window.innerWidth < 768)
		window.addEventListener('resize', handler, { passive: true })
		return () => window.removeEventListener('resize', handler)
	}, [])
	return isMobile
}

function RouteComponent() {
	const { token: tokenString } = Route.useParams()
	const isMobile = useIsMobile()
	const queryClient = useQueryClient()
	const { t, i18n, ready } = useTranslation('booking', { useSuspense: false })

	const [candidateTimezone, setCandidateTimezone] = useState(
		() => Intl.DateTimeFormat().resolvedOptions().timeZone,
	)
	const [selectedSlot, setSelectedSlot] = useState<SelectedSlot | null>(null)
	const [isExpiredLocally, setIsExpiredLocally] = useState(false)
	const [conflictBanner, setConflictBanner] = useState<string | null>(null)
	const [justConfirmed, setJustConfirmed] = useState(false)
	const [alreadyBooked, setAlreadyBooked] = useState(false)
	const [languageChosen, setLanguageChosen] = useState(false)

	const showQuery = usePublicBookingShow(tokenString)
	const bookingData = showQuery.data

	// Default the page to the campaign language, unless the candidate has picked
	// one via the switcher. The language chunk loads on demand (see i18n.ts).
	const campaignLanguage = bookingData?.language
	useEffect(() => {
		if (languageChosen) { return }
		const target = resolveBookingLanguage(campaignLanguage)
		if (target !== i18n.language) {
			i18n.changeLanguage(target).catch((error) => {
				logger.warn('Failed to switch booking page language', {
					category: 'booking',
					data: { target, error },
				})
			})
		}
	}, [campaignLanguage, languageChosen, i18n])

	const currentLanguage: SupportedLanguage = (SUPPORTED_LANGUAGES as readonly string[]).includes(i18n.language)
		? (i18n.language as SupportedLanguage)
		: DEFAULT_LANGUAGE

	function handleLanguageChange(language: SupportedLanguage) {
		setLanguageChosen(true)
		i18n.changeLanguage(language).catch((error) => {
			logger.warn('Failed to switch booking page language', {
				category: 'booking',
				data: { language, error },
			})
		})
	}

	const regionsQuery = usePublicBookingRegions(tokenString, {
		enabled: bookingData?.status === 'pending',
		staleTime: 2 * 60 * 1000,
	})

	const regions = regionsQuery.data?.regions
		? parseRegions(
			regionsQuery.data.regions.map((region) => ({
				placeable_from: region.placeableFrom,
				placeable_to: region.placeableTo,
			})),
		)
		: []

	const calendarAnchor = regions.length > 0
		? findFirstWeekWithRegions(regions, candidateTimezone) ?? new Date()
		: new Date()

	const confirmMutation = usePublicBookingConfirm()

	function handleConfirm() {
		if (!selectedSlot) { return }
		setConflictBanner(null)

		confirmMutation.mutate(
			{
				tokenString,
				data: {
					scheduledAt: slotToIso(selectedSlot),
					candidateTimezone,
				},
			},
			{
				onSuccess() {
					setJustConfirmed(true)
				},
				onError(error: AxiosError<PublicBookingConfirmError>) {
					if (!error.response) {
						setConflictBanner(t('errors.no_response'))
						return
					}

					const body = error.response.data

					if (!body || !('error' in body)) {
						if (error.response.status >= 500) {
							setConflictBanner(t('errors.server_error'))
						} else {
							setConflictBanner(t('errors.generic'))
						}
						return
					}

					if (body.error === 'already_booked') {
						setAlreadyBooked(true)
						return
					}

					if (body.error === 'expired') {
						setIsExpiredLocally(true)
						return
					}

					if (body.error === 'server_busy') {
						setConflictBanner(t('errors.server_busy'))
						return
					}

					setSelectedSlot(null)
					queryClient.invalidateQueries({ queryKey: publicBookingRegionsQueryKey(tokenString) })

					if (body.error === 'slot_taken') {
						setConflictBanner(t('errors.slot_taken'))
						return
					}

					if (body.error === 'invalid_slot') {
						setConflictBanner(t('errors.invalid_slot'))
						return
					}

					setConflictBanner(t('errors.server_error'))
					logger.warn('Unexpected public booking confirm error', {
						category: 'booking',
						data: { error: body.error, status: error.response.status },
					})
				},
			},
		)
	}

	// Gate on `ready` too, so the on-demand booking namespace (and the campaign
	// language) has loaded before any translated copy renders — no English flash.
	if (showQuery.isLoading || !ready) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-gray-600" />
			</div>
		)
	}

	if (showQuery.isError) {
		const is404 = (showQuery.error as { response?: { status?: number } })?.response?.status === 404
		return (
			<div className="flex min-h-screen flex-col items-center justify-center gap-2 text-center">
				{is404
					? (
						<>
							<p className="text-lg font-medium text-gray-800">{t('load.not_found_title')}</p>
							<p className="text-sm text-gray-500">{t('load.not_found_body')}</p>
						</>
					)
					: (
						<>
							<p className="text-lg font-medium text-gray-800">{t('load.error_title')}</p>
							<p className="text-sm text-gray-500">{t('load.error_body')}</p>
						</>
					)}
			</div>
		)
	}

	if (!bookingData) {
		return null
	}

	if (isExpiredLocally) {
		const recruiterName = bookingData.recruiterName ?? t('terminal.recruiter_fallback')
		return (
			<TerminalStateScreen
				status="expired"
				message={t('terminal.expired_with_recruiter', { recruiter: recruiterName })}
			/>
		)
	}

	if (justConfirmed) {
		return <TerminalStateScreen status="confirmed" />
	}

	if (alreadyBooked || bookingData.status === 'booked') {
		return <TerminalStateScreen status="booked" />
	}

	if (bookingData.status !== 'pending') {
		return <TerminalStateScreen status={bookingData.status} />
	}

	const hasNoAvailability = !regionsQuery.isLoading && !regionsQuery.isError && regions.length === 0

	return (
		<div className="min-h-screen bg-white">
			<div className="mx-auto max-w-4xl px-4 py-8">
				<div className="mb-4 flex justify-end">
					<BookingLanguageSelector value={currentLanguage} onChange={handleLanguageChange} />
				</div>

				<BookingHeader token={bookingData} />

				{bookingData.expiresAt != null && (
					<div className="mt-4">
						<BookingExpiryCountdown
							expiresAt={bookingData.expiresAt}
							onExpired={() => setIsExpiredLocally(true)}
						/>
					</div>
				)}

				<div className="mt-6 flex flex-wrap items-center justify-between gap-3">
					<p className="text-sm font-medium text-gray-700">{t('timezone_label')}</p>
					<TimezoneSelector
						value={candidateTimezone}
						onChange={(tz) => {
							setCandidateTimezone(tz)
							setSelectedSlot(null)
						}}
					/>
				</div>

				{conflictBanner != null && (
					<div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
						{conflictBanner}
					</div>
				)}

				{regionsQuery.isLoading
					? (
						<div className="mt-8 flex justify-center">
							<div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-gray-600" />
						</div>
					)
					: regionsQuery.isError
					? (
						<div className="mt-8 rounded-xl border border-red-100 bg-red-50 py-12 text-center">
							<p className="text-sm font-medium text-gray-600">{t('load.times_error_title')}</p>
							<p className="mt-1 text-sm text-gray-400">
								{t('load.times_error_body')}
							</p>
						</div>
					)
					: hasNoAvailability
					? (
						<div className="mt-8 rounded-xl border border-gray-100 bg-gray-50 py-12 text-center">
							<p className="text-sm font-medium text-gray-600">{t('load.no_times_title')}</p>
							<p className="mt-1 text-sm text-gray-400">
								{t('load.no_times_body')}
							</p>
						</div>
					)
					: (
						<div className="mt-6">
							{isMobile
								? (
									<CalendarDayColumns
										regions={regions}
										durationMinutes={bookingData.durationMinutes ?? 30}
										candidateTimezone={candidateTimezone}
										selectedSlot={selectedSlot}
										onSlotSelect={setSelectedSlot}
										initialAnchor={calendarAnchor}
									/>
								)
								: (
									<CalendarWeekView
										regions={regions}
										durationMinutes={bookingData.durationMinutes ?? 30}
										candidateTimezone={candidateTimezone}
										selectedSlot={selectedSlot}
										onSlotSelect={setSelectedSlot}
										initialAnchor={calendarAnchor}
									/>
								)}
						</div>
					)}

				{selectedSlot != null && bookingData.durationMinutes != null && (
					<div className="mt-4">
						<BookingConfirmPanel
							slot={selectedSlot}
							durationMinutes={bookingData.durationMinutes}
							candidateTimezone={candidateTimezone}
							isSubmitting={confirmMutation.isPending}
							onConfirm={handleConfirm}
							onClear={() => setSelectedSlot(null)}
						/>
					</div>
				)}
			</div>
		</div>
	)
}
